import type { CurveCellFlags, IdentityCurveDraft, IdentityFieldName } from './FilterIdentityCurveFields';

/**
 * FS-663: the pure rules behind the label-proposal review panel's
 * cross-brand handling. The panel (LabelProposalsPanel) wires these to the
 * form, the keyed `GET /label-proposals/:id?…` check and the accept body;
 * nothing here touches React or the network.
 */

/** A live catalog row the reviewed identity may already be. `same_brand`
 * siblings share the brand canon (the pre-FS-663 family); `model_number`
 * siblings sit under another brand canon and share a product number token
 * with the proposal; a null `match_reason` is a row the server did not
 * annotate — it is listed, but no rule that reasons about WHY it is here
 * applies to it. `exact_identity` is the server's verdict that brand,
 * size, depth, MERV and number all equal the reviewed identity. */
export interface ConflictSibling {
  id: number;
  brand: string;
  brand_canon: string | null;
  model: string;
  maedb_model_number: string | null;
  nominal_size: string;
  depth_inches: number | null;
  merv: number | null;
  source: string;
  source_date: string | null;
  certified: boolean;
  curve_point_count: number;
  match_reason: 'same_brand' | 'model_number' | null;
  matched_token: string | null;
  matched_from: 'manual_model' | 'extracted' | 'reviewed_number' | 'reviewed_model' | null;
  exact_identity: boolean;
}

export type ConflictResolution =
  | { action: 'update_existing'; catalog_id: number }
  | { action: 'create_new' };

/** One brand spelling an accept may turn into an alias (the GET's
 * `alias_preview`): the submitted candidate's and the reviewed one, each
 * with the canon it resolves to today and whether it is an established
 * brand (which an accept never redirects). */
export interface AliasPreviewEntry {
  origin: 'submitted' | 'reviewed';
  spelling: string;
  alias_key: string;
  current_canon: string | null;
  established: boolean;
}

/** The six identity fields in the order the GET query, the key and the
 * server's echo all use. */
export const IDENTITY_FIELDS: readonly IdentityFieldName[] = [
  'brand', 'nominal_size', 'depth_inches', 'merv', 'maedb_model_number', 'model',
];
export type ReviewedIdentity = Record<IdentityFieldName, string>;

/** The identity the form currently describes: the six fields, trimmed.
 * Manufacturer, note and curve are not identity — editing them never
 * changes the key. */
export const reviewedIdentity = (
  draft: Pick<IdentityCurveDraft, IdentityFieldName>,
): ReviewedIdentity => ({
  brand: draft.brand.trim(),
  nominal_size: draft.nominal_size.trim(),
  depth_inches: draft.depth_inches.trim(),
  merv: draft.merv.trim(),
  maedb_model_number: draft.maedb_model_number.trim(),
  model: draft.model.trim(),
});

/** The key a `GET /label-proposals/:id?…` is issued for. The server
 * computes the same string from the six effective fields it received and
 * echoes it as `identity_key`; the panel compares the strings and drops any
 * response whose key is not the current one. Contract: a JSON array of the
 * six trimmed values in IDENTITY_FIELDS order — no normalization, so a size
 * typed as `16" x 25" x 5"` keys as typed (the server reports the
 * normalization failure in `identity_errors` under that same key). */
export const identityKey = (identity: ReviewedIdentity): string =>
  JSON.stringify(IDENTITY_FIELDS.map(f => identity[f]));

/** Query string for the keyed GET. Every field is always sent: an omitted
 * parameter would mean "use the stored candidate", and an explicitly empty
 * `model` / `maedb_model_number` means "cleared". */
export const identityQuery = (identity: ReviewedIdentity): string => {
  const p = new URLSearchParams();
  for (const f of IDENTITY_FIELDS) p.set(f, identity[f]);
  return p.toString();
};

/** Product numbers compare on the collapsed form: `HCF16-11` ≡ `HCF1611`. */
export const collapseNumber = (s: string | null | undefined): string =>
  (s ?? '').trim().toLowerCase().replace(/-/g, '');

export type SiblingMatch =
  | { kind: 'exact'; text: string }
  | { kind: 'model_number'; token: string; brand: string; text: string }
  | { kind: 'same_brand'; text: string }
  | { kind: 'unannotated'; text: string };

/** How to explain why a sibling is listed. The panel renders the
 * `model_number` parts with the token in italics and the brand in bold;
 * `text` is the same sentence flat, for tests and titles. */
export const siblingMatch = (s: ConflictSibling): SiblingMatch => {
  if (s.exact_identity) {
    return { kind: 'exact', text: 'exact match for the reviewed identity' };
  }
  if (s.match_reason === 'model_number') {
    const token = s.matched_token ?? s.maedb_model_number ?? '';
    return {
      kind: 'model_number', token, brand: s.brand,
      text: `same model number ${token} under a different brand name (${s.brand})`,
    };
  }
  if (s.match_reason === 'same_brand') {
    return {
      kind: 'same_brand',
      text: 'same brand, size and MERV under a different MAEDbS model number',
    };
  }
  return { kind: 'unannotated', text: '' };
};

/** Same-brand default (FS-504 D6, unchanged apart from comparing collapsed
 * numbers): a blank MAEDbS field against a single sibling is almost
 * certainly the same filter (update it); a distinct typed number against
 * NUMBERED siblings is a genuinely different product (add it separately). A
 * typed number against only blank-numbered siblings gets NO default: the
 * number may simply be the first filing of the same product, and
 * preselecting "add separate" invites a one-click twin — the reviewer
 * chooses. */
export const defaultResolution = (
  siblings: ConflictSibling[], typedMaedb: string | null,
): ConflictResolution | null => {
  if (!siblings.length) return null;
  const typed = collapseNumber(typedMaedb);
  if (!typed && siblings.length === 1) {
    return { action: 'update_existing', catalog_id: siblings[0].id };
  }
  if (typed && siblings.every(s => collapseNumber(s.maedb_model_number) !== typed)) {
    if (siblings.every(s => !s.maedb_model_number)) return null;
    return { action: 'create_new' };
  }
  return null;
};

/** What the panel selects on its own when a keyed check (or a 409) lands.
 *
 * - The server's `exact_identity` forces the selection — never number
 *   equality, so two cross-brand siblings sharing a number both stay
 *   selectable.
 * - An unannotated sibling (null `match_reason`) is not same-brand: with
 *   one in the list, nothing else is preselected.
 * - Same-brand siblings alone: the D6 rule above.
 * - A `model_number` sibling is preselected only when it is the only
 *   sibling of any kind, the token that found it came from the reviewed
 *   number or model (or, with a blank reviewed number, from the extraction —
 *   a reviewed number that restates the extracted token is not a
 *   contradiction), and the target's number is blank or equal after
 *   collapse. Evidence the reviewer has since contradicted makes the sibling
 *   advisory only; so does a mixed list — model-number siblings keep an
 *   explicit choice. */
export const preselectResolution = (
  siblings: ConflictSibling[], reviewedNumber: string | null,
): ConflictResolution | null => {
  if (!siblings.length) return null;
  const exact = siblings.find(s => s.exact_identity);
  if (exact) return { action: 'update_existing', catalog_id: exact.id };
  if (siblings.some(s => s.match_reason == null)) return null;
  if (siblings.every(s => s.match_reason !== 'model_number')) {
    return defaultResolution(siblings, reviewedNumber);
  }
  if (siblings.length !== 1) return null;
  const s = siblings[0];
  const typed = collapseNumber(reviewedNumber);
  const evidenceStands =
    s.matched_from === 'reviewed_number' || s.matched_from === 'reviewed_model' ||
    (s.matched_from === 'extracted' &&
      (!typed || collapseNumber(s.matched_token) === typed));
  const targetAgrees =
    !s.maedb_model_number || collapseNumber(s.maedb_model_number) === typed;
  if (evidenceStands && targetAgrees) {
    return { action: 'update_existing', catalog_id: s.id };
  }
  return null;
};

/** The spellings an accept would record as aliases of `finalCanon` — the
 * canon the accept publishes under: the chosen update target's, else the
 * reviewed brand's. A new spelling whose canon differs is recorded; an
 * established brand never is (a corrected brand is not a side effect of one
 * accept); a spelling already mapping there is idempotent. Whatever found
 * the target — same brand, model number, exact match — the submitted
 * spelling is still written, so the consent box keys on this, not on the
 * match reason. */
export const aliasesToRecord = (
  preview: AliasPreviewEntry[], finalCanon: string | null,
): AliasPreviewEntry[] => {
  if (!finalCanon) return [];
  return preview.filter(e => !e.established && e.current_canon !== finalCanon);
};

/** "Also treat 'A' as Lennox for future matching"; two spellings are joined
 * with "and". */
export const aliasConsentLabel = (spellings: string[], finalBrand: string): string => {
  const quoted = spellings.map(s => `'${s}'`);
  const list = quoted.length <= 1
    ? quoted.join('')
    : `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
  return `Also treat ${list} as ${finalBrand} for future matching`;
};

// ── FS-674: recovery of a failed photo import ────────────────────────────────

/** The server's verdict on one recovery action for a job (`recovery` on
 * `GET /import-jobs`): available, or blocked by a stable code naming the
 * job and/or proposal that stands in the way. */
export interface RecoveryBlock {
  blocked: string;
  job_id?: number;
  proposal_id?: number;
}
export type RecoveryVerdict = 'available' | RecoveryBlock;
export interface JobRecovery {
  retry: RecoveryVerdict;
  correct: RecoveryVerdict;
}

/** Why a blocked action is blocked, for the row: names the job or proposal
 * to look at. `jobId` is the row's own id, so a correction on this very
 * job reads "corrected → proposal P" rather than naming itself. */
export const recoveryBlockText = (block: RecoveryBlock, jobId: number): string => {
  switch (block.blocked) {
    case 'retry_in_progress':
      return `read in progress: job ${block.job_id}`;
    case 'already_retried':
      return `retried as ${block.job_id}` +
        (block.proposal_id != null ? ` → proposal ${block.proposal_id}` : '');
    case 'already_corrected':
      return (block.job_id != null && block.job_id !== jobId ? `job ${block.job_id} ` : '') +
        `corrected → proposal ${block.proposal_id}`;
    case 'nothing_to_correct':
      return 'nothing to correct — the job failed before a trustworthy read; retry the read, or use Enter manually';
    case 'not_failed':
      return 'not a failed job';
    default:
      return `blocked: ${block.blocked}`;
  }
};

export interface RecoveryAction {
  available: boolean;
  /** The blocking text, or null when there is nothing to say (available,
   * or a server that does not send `recovery`). */
  reason: string | null;
}
export interface RecoveryActions {
  retry: RecoveryAction;
  correct: RecoveryAction;
}

/** What the row offers. Availability comes from the server's `recovery`
 * field ONLY — never from the job's status, its ids, or memory of clicks.
 * A job the server sent without `recovery` offers nothing. */
export const recoveryActions = (
  job: { id: number; recovery?: JobRecovery | null },
): RecoveryActions => {
  const one = (verdict: RecoveryVerdict | undefined): RecoveryAction => {
    if (verdict == null) return { available: false, reason: null };
    if (verdict === 'available') return { available: true, reason: null };
    return { available: false, reason: recoveryBlockText(verdict, job.id) };
  };
  return { retry: one(job.recovery?.retry), correct: one(job.recovery?.correct) };
};

/** Which cells of a curve row the read flagged (FS-665 diagnostics) — the
 * field block's own type, re-exported for the panel and its tests. */
export type { CurveCellFlags };

export type CurveRowDraft = { resistance_iwc: string; cfm: string };

/** The identity the model read, as `GET /import-jobs/:id` returns it. */
export interface RawExtractionIdentity {
  brand?: string | null;
  model_number?: string | null;
  nominal_size?: string | null;
  depth_inches?: number | null;
  merv?: number | null;
}

/** One point of the API's extraction diagnostics, as far as the prefill
 * reads it: the converted values (null when the unit was unsupported) and
 * the cell each flag marks. */
export interface DiagnosticPointLike {
  resistance_iwc: number | null;
  cfm: number | null;
  flags: { field: 'static_pressure' | 'airflow' }[];
}

export interface CorrectionPrefill {
  draft: IdentityCurveDraft;
  flags: CurveCellFlags[];
}

const printedNumber = (v: number | null | undefined): string =>
  (v == null || !Number.isFinite(v) ? '' : String(v));

/** The Correct-by-hand draft: identity from the raw extraction (the same
 * `model_number` → MAEDbS-number mapping the callback uses; depth BLANK
 * when the read had none, never the form's default), curve rows from the
 * diagnostics in extraction order — the converted values as strings, blank
 * when a value is null — never guessed, deduplicated or sorted. Each row
 * carries the read's flags for its two cells; the form paints those, not
 * the identity-only `fieldErrors`. Fewer than two rows are padded with
 * blank ones so the form is usable. */
export const correctionPrefill = (
  raw: RawExtractionIdentity | null | undefined,
  diagnostics: { points: DiagnosticPointLike[] } | null | undefined,
): CorrectionPrefill => {
  const points = diagnostics?.points ?? [];
  const curve: CurveRowDraft[] = points.map(p => ({
    resistance_iwc: printedNumber(p.resistance_iwc),
    cfm: printedNumber(p.cfm),
  }));
  const flags: CurveCellFlags[] = points.map(p => ({
    resistance_iwc: p.flags.some(f => f.field === 'static_pressure'),
    cfm: p.flags.some(f => f.field === 'airflow'),
  }));
  while (curve.length < 2) {
    curve.push({ resistance_iwc: '', cfm: '' });
    flags.push({ resistance_iwc: false, cfm: false });
  }
  return {
    draft: {
      brand: raw?.brand ?? '',
      manufacturer: '',
      model: '',
      nominal_size: raw?.nominal_size ?? '',
      depth_inches: printedNumber(raw?.depth_inches),
      merv: printedNumber(raw?.merv),
      maedb_model_number: raw?.model_number ?? '',
      curve,
    },
    flags,
  };
};

/** The structural curve rules, run locally on a resistance-sorted COPY
 * exactly as the API's validateReviewCurve does (finite numbers, positive,
 * at least two points, no duplicate resistance, no duplicate CFM, CFM
 * strictly increasing with resistance) — the display order is never
 * touched. The messages are the server's. Range warnings are not errors
 * and are not produced here. A blank or non-numeric cell is the server's
 * "not a list of numeric points" (it never reaches the server: submit is
 * disabled while this is non-empty). */
export const curveStructuralErrors = (curve: CurveRowDraft[]): string[] => {
  const coerced: { resistance_iwc: number; cfm: number }[] = [];
  for (const row of curve) {
    const r = row.resistance_iwc.trim();
    const c = row.cfm.trim();
    const rn = r === '' ? NaN : Number(r);
    const cn = c === '' ? NaN : Number(c);
    if (!Number.isFinite(rn) || !Number.isFinite(cn)) {
      return ['curve is not a list of numeric (resistance_iwc, cfm) points'];
    }
    coerced.push({ resistance_iwc: rn, cfm: cn });
  }
  const errors: string[] = [];
  if (coerced.length < 2) errors.push('curve needs at least 2 points to interpolate');
  for (const p of coerced) {
    if (p.resistance_iwc <= 0) errors.push(`resistance ${p.resistance_iwc} is not positive`);
    if (p.cfm <= 0) errors.push(`cfm ${p.cfm} is not positive`);
  }
  const sorted = [...coerced].sort((a, b) => a.resistance_iwc - b.resistance_iwc);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].resistance_iwc === sorted[i - 1].resistance_iwc) {
      errors.push(`duplicate resistance value ${sorted[i].resistance_iwc}`);
    }
    if (sorted[i].cfm === sorted[i - 1].cfm) {
      errors.push(`duplicate cfm value ${sorted[i].cfm}`);
    } else if (sorted[i].cfm < sorted[i - 1].cfm) {
      errors.push('cfm must strictly increase with resistance');
    }
  }
  return [...new Set(errors)];
};

const NO_FLAGS: CurveCellFlags = { resistance_iwc: false, cfm: false };

/** Carry the per-row flags across an edit of the curve rows. The field
 * block hands back a whole next draft, so the change is inferred: a row
 * added at the end gets no flags; a removed row takes its flags with it
 * (untouched rows keep their object identity, so the removed one is the
 * first that no longer matches); an edited cell clears its own flag and
 * nothing else. */
export const reconcileCurveFlags = (
  prev: CurveRowDraft[], next: CurveRowDraft[], flags: CurveCellFlags[],
): CurveCellFlags[] => {
  const padded = prev.map((_, i) => flags[i] ?? NO_FLAGS);
  if (next.length > prev.length) {
    return [...padded, ...Array.from({ length: next.length - prev.length }, () => ({ ...NO_FLAGS }))];
  }
  if (next.length < prev.length) {
    let removed = prev.findIndex((row, i) => next[i] !== row);
    if (removed < 0) removed = prev.length - 1;
    return padded.filter((_, i) => i !== removed);
  }
  return padded.map((f, i) => ({
    resistance_iwc: f.resistance_iwc && next[i].resistance_iwc === prev[i].resistance_iwc,
    cfm: f.cfm && next[i].cfm === prev[i].cfm,
  }));
};
